/**
 * Flow C patient quote — GET /api/appointments/{id}/quote?token=. Rate, floor, cap, the
 * ceiling in money, the flat no-show fee, and `authState` (contract §2), which is what
 * `ConsultJoinButton`/`ConsultRoomFrame` gate the join door on.
 *
 * States mirror the other gateway hooks: loading is `data === null && !error`; error
 * carries `{code, raw}` (409 PRICING_NOT_SET / PROVIDER_NOT_BILLABLE per contract §2).
 *
 * `gatewayApi` is an optional trailing param defaulting to the module singleton,
 * mirroring the component-level DI rule (contract §9).
 */
import { useCallback, useEffect, useState } from "react";
import defaultGatewayApi from "../api";
import { extractGatewayErrorCode } from "../errors";

export function useConsultQuote(appointmentId, token, gatewayApi = defaultGatewayApi) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!appointmentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const quote = await gatewayApi.getConsultQuote(appointmentId, token);
      setData(quote);
      setError(null);
    } catch (e) {
      setError({ code: extractGatewayErrorCode(e), raw: e });
    } finally {
      setLoading(false);
    }
  }, [appointmentId, token, gatewayApi]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, reload };
}

export default useConsultQuote;
